import { Box, IconButton, Typography } from '@mui/material';
import { KeyboardBackspace } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { tableStructure } from './tableStructure';
import { ESortDirection, ETableStructureType } from '../../../ui/EasTable/types';
import { getJotforms, getJotformsLoad } from '../../../redux/reducers/jotform/selectors';
import { getJotformsRequest } from '../../../redux/reducers/jotform/reducer';
import { PAGE_SIZE } from '../../../services/constants/env';
import { TFilters, TOrderBy } from '../../../redux/types';
import { DATES_ARRAY, DATES_ID } from '../../../services/constants/dates-constants';
import { EasTableWithFilter } from '../../../ui/EasTableWithFilter';
import { RegisterEmDialog } from './RegisterEmDialog';
import { PATHS } from '../../../routes/constants';
import { getSavedUrl } from '../../../redux/reducers/auth/selectors';

export const EMManagementCreate = () => {
	const { search } = useLocation();
	const savedUrl = useSelector(getSavedUrl);
	const navigate = useNavigate();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);

	const dispatch = useDispatch();
	const jotforms = useSelector(getJotforms);
	const jotformsLoading = useSelector(getJotformsLoad);

	const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
	const [selectedJotformId, setSelectedJotformId] = useState<number | null>(null);

	const handleReplayHistory = () => {
		if (savedUrl && savedUrl !== window.location.pathname) {
			if (savedUrl.includes(PATHS.EM_MANAGEMENT_PAGE)) navigate(savedUrl);
		} else navigate(PATHS.EM_MANAGEMENT_PAGE);
	};

	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector] && item.type === ETableStructureType.DATE) {
			if (navigateParams[item.selector] === DATES_ID.CUSTOM_RANGE) {
				if (navigateParams[`${String(item.selector)}StartDate`]) {
					acc.push({
						fieldName: item.selector,
						value: navigateParams[`${String(item.selector)}StartDate`] as string,
						operator: '>=',
					});
				}
				if (navigateParams[`${String(item.selector)}EndDate`]) {
					acc.push({
						fieldName: item.selector,
						value: navigateParams[`${String(item.selector)}EndDate`] as string,
						operator: '<=',
					});
				}
				return acc;
			}
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}

		if (navigateParams[item.selector as string])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector as string] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		const orderBy = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC
					: ESortDirection.DESC,
			} as TOrderBy);
		}
		orderBy.push({
			fieldName: 'id',
			direction: ESortDirection.DESC,
		} as TOrderBy);
		dispatch(
			getJotformsRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleAction = (id: number, actionName: string) => {
		if (actionName === 'Register EM') {
			setSelectedJotformId(id);
			setIsRegisterDialogOpen(true);
		}
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter
				tableStructure={tableStructure}
				items={jotforms}
				itemsIsLoading={jotformsLoading}
				handleAction={handleAction}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box
						sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: 0, cursor: 'pointer' }}
						onClick={handleReplayHistory}
					>
						<IconButton
							color="primary"
							sx={{
								p: '0',
							}}
						>
							<KeyboardBackspace />
						</IconButton>
						<Typography
							sx={{
								fontSize: '.9rem',
								color: 'primary.main',
								fontWeight: 'bold',
							}}
						>
							Create EM
						</Typography>
					</Box>
				</Box>
			</EasTableWithFilter>

			<RegisterEmDialog
				open={isRegisterDialogOpen}
				setClose={() => setIsRegisterDialogOpen(false)}
				jotformId={selectedJotformId}
			/>
		</Box>
	);
};
